<template>
    <Spinner v-if="displaySpinner" />
    <form class="verify-email">
        <h2 class="text-l">Email Verification Required</h2>
        <div class="verification-steps">
            <div class="step">
                <span class="step-number">1</span>
                <p>Click the button below to receive a verification email</p>
            </div>
            <div class="step">
                <span class="step-number">2</span>
                <p>Open the email and click the verification link</p>
            </div>
            <div class="step">
                <span class="step-number">3</span>
                <p>Return to this window to continue automatically</p>
            </div>
        </div>
        <Message v-if="errorMessage" :closable="false" severity="error">
            {{ errorMessage }}
        </Message>
        <Message v-if="successMessage" :closable="false" severity="success">
            {{ successMessage }}
        </Message>
        <div class="buttons">
            <Button
                :label="
                    cooldown > 0
                        ? `Wait ${cooldown}s`
                        : 'Send Verification Email'
                "
                :disabled="cooldown > 0"
                raised
                primary
                @click="sendVerificationEmail_"
            />
            <Button label="Back" raised @click="close" />
        </div>
    </form>
</template>

<script lang="ts" setup>
import { inject, Ref, ref, onMounted, onUnmounted, watch } from "vue";
import Spinner from "@/shared/Spinner.vue";
import Message from "primevue/message";
import Button from "primevue/button";
import { useAuthenticationStore } from "@/stores/authentication-store";
import type { DynamicDialogInstance } from "primevue/dynamicdialogoptions";

const dialogRef = inject<Ref<DynamicDialogInstance>>("dialogRef");
const { sendVerificationEmail, checkEmailVerification } =
    useAuthenticationStore();

const isPageInFocus = ref(true);
const displaySpinner = ref(false);
const errorMessage = ref("");
const successMessage = ref("");
const cooldown = ref(0);
const verificationCheckInterval = ref<number>();
const { successCallback, failureCallback } = dialogRef!.value.data;

watch(isPageInFocus, (focus) => {
    if (focus && verificationCheckInterval.value) {
        checkVerification();
    }
});

const startCooldown = () => {
    cooldown.value = 60;
    const timer = setInterval(() => {
        cooldown.value--;
        if (cooldown.value <= 0) {
            clearInterval(timer);
        }
    }, 1000);
};

const checkVerification = async () => {
    if (!isPageInFocus.value) return;

    try {
        const verified = await checkEmailVerification();
        if (verified) {
            if (verificationCheckInterval.value) {
                clearInterval(verificationCheckInterval.value);
                verificationCheckInterval.value = undefined;
            }
            successCallback();
            close();
        }
    } catch (error) {
        console.error("Error checking verification status:", error);
    }
};

const startVerificationCheck = () => {
    verificationCheckInterval.value = window.setInterval(
        checkVerification,
        5000
    );
    // Run initial check
    checkVerification();
};

const close = () => dialogRef!.value.close();

const sendVerificationEmail_ = async () => {
    if (cooldown.value > 0) return;
    displaySpinner.value = true;
    try {
        await sendVerificationEmail();
        successMessage.value =
            "Verification email sent successfully. Please check your email and click the verification link.";
        startCooldown();
        startVerificationCheck();
    } catch (error) {
        errorMessage.value =
            "Failed to send verification email. Please try again.";
        failureCallback();
        throw error;
    } finally {
        displaySpinner.value = false;
    }
};

const focus = () => (isPageInFocus.value = true);
const blur = () => (isPageInFocus.value = false);

onMounted(async () => {
    window.addEventListener("blur", blur);
    window.addEventListener("focus", focus);

    // Only start verification check if email is not already verified
    const isVerified = await checkEmailVerification();
    if (!isVerified) {
        startVerificationCheck();
    } else {
        // Email already verified, close the dialog with success
        successCallback();
        close();
    }
});

onUnmounted(() => {
    window.removeEventListener("blur", blur);
    window.removeEventListener("focus", focus);
    if (verificationCheckInterval.value) {
        clearInterval(verificationCheckInterval.value);
        verificationCheckInterval.value = undefined;
    }
});
</script>

<style lang="scss">
.text-l {
    font-size: 1.5rem;
    margin-bottom: 2rem;
    color: var(--primary-color);
}

.verify-email {
    text-align: center;
    padding: 2rem;
    max-width: 600px;
    margin: 0 auto;
}

.verification-steps {
    display: flex;
    flex-direction: column;
    margin: 2rem 0;
    text-align: left;

    .step {
        display: flex;
        align-items: center;
        gap: 1rem;
        padding: 1rem;
        background: var(--surface-ground);
        border-radius: 8px;

        .step-number {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 32px;
            height: 32px;
            background: var(--stekz-primary-color);
            color: white;
            border-radius: 50%;
            font-weight: bold;
        }

        p {
            margin: 0;
            font-size: 1.1rem;
        }
    }
}

.verification-success {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
    padding: 2rem;

    .pi-check-circle {
        font-size: 4rem;
        color: var(--green-500);
    }

    h2 {
        color: var(--green-500);
        margin: 0;
    }

    p {
        color: var(--text-color-secondary);
    }
}

.buttons {
    display: flex;
    flex-direction: row;
    gap: 1rem;
    justify-content: center;
    margin-top: 2rem;
}
</style>
